
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { PrescriptionMutation, PrescriptionState } from '@/store/Prescription/PrescriptionRoot';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import ErrorService from '@/utils/ErrorService';

// Enums
import OrganisationRoleEnum from '@/ship/Enums/OrganisationRoleEnum';

// Utils
import { cloneDeep, find, sortBy, uniqBy } from 'lodash';
import naturalSort from 'javascript-natural-sort';
import UserService from '@/utils/UserService';

// Interfaces
import City from '@/ship/Models/City';
import TypeOfWork from '@/ship/Models/TypeOfWork';
import Violation from '@/ship/Models/Violation';
import User from '@/ship/Models/User';
import MainPackData from '@/ship/Models/MainPackData';
import IPrescriptionFilter from '@/ship/Models/IPrescriptionFilter';
import { FiltersAcceptance } from '@/ship/Models/Acceptance';

// Components
import { VGridCol, VGridRow } from 'vui';
import PrescriptionFilterButtons from './PrescriptionFilterButtons.vue';
import { DateTime } from 'luxon';

// Api
import { getFiltersAcceptance, getFiltersWarranty } from '@/utils/api/Prescriptions';
import { reduceByValue } from '@/utils/MultiselectHelper';

@Component({
    components: {
        VGridCol,
        VGridRow,
        PrescriptionFilterButtons,
    },
})
export default class PrescriptionFilter extends Vue {
    @Prop({ type: Boolean, default: false })
    public isMobile!: boolean;

    @Prop({ type: Function, default: () => ({}) })
    public hideFilter!: () => void;

    @Action GET_USERS!: () => User[];
    @State users!: User[];
    @State city!: City;
    @State types!: TypeOfWork[];
    @PrescriptionState violations!: Violation[];
    @PrescriptionState mainPackData!: MainPackData | null;
    @PrescriptionMutation RESET_VIOLATIONS!: () => void;

    readonly reduceByValue = reduceByValue;

    filters: FiltersAcceptance = {
        authors: [],
        buildingNumbers: [],
        cities: [],
        customers: [],
        floors: [],
        generalContractors: [],
        postalNumbers: [],
        projects: [],
        realtyTypes: [],
        remarkTypes: [],
        sections: [],
        status: [],
        flats: [],
    };

    @Ref() refFind!: HTMLButtonElement;

    readonly defaultFiltersModel: IPrescriptionFilter = {
        'authorId': null,
        'cityId': null,
        'projectId': null,
        'object': '',
        'statusId': [],
        'typeOfWorkId': null,
        'section.name': '',
        'floor.name': '',
        'executor.id': null,
        'generalContractorId': null,
        'subcontractorId': null,
        'drawingTypeId': null,
        'deadline': '',
        'realtyType': '',
        'customerId': null,
        'buildingNumber': '',
        'postalNumber': '',
        'apartmentNumber': null,
    };

    filter: IPrescriptionFilter = cloneDeep(this.defaultFiltersModel);

    deadlineConfig = {
        mode: 'range',
        altFormat: 'd.m.Y',
        altInput: true,
        locale: Russian,
        disableMobile: 'true',
    };

    prescriptionTypes = [
        {
            value: 'Violation',
            label: 'Строительство',
        },
        {
            value: 'ViolationAcceptance',
            label: 'Приемка',
        },
        {
            value: 'ViolationGuarantee',
            label: 'Гарантия',
        },
    ];

    get generalContractors() {
        return this.mainPackData?.organisations.filter((organisation) =>
            organisation.roles?.includes(OrganisationRoleEnum.GeneralContractor),
        );
    }

    get subcontractors() {
        return this.mainPackData?.organisations.filter((organisation) =>
            organisation.roles?.includes(OrganisationRoleEnum.Subcontractor),
        );
    }

    get route() {
        return { name: 'prescription.create', query: this.$route.query };
    }

    get sortGeneral() {
        if (!this.generalContractors) return [];

        return this.generalContractors
            .map((name) => ({
                value: name.id,
                label: name.shortName,
            }))
            .sort((a, b) => naturalSort(a.label, b.label));
    }

    get drawingType() {
        if (!this.mainPackData) {
            return null;
        }

        return this.mainPackData.drawingTypes.map((type) => ({
            value: type.id,
            label: type.name,
        }));
    }

    get sortSubcontractor() {
        if (!this.subcontractors) return [];

        return this.subcontractors
            .map((name) => ({
                value: name.id,
                label: name.shortName,
            }))
            .sort((a, b) => naturalSort(a.label, b.label));
    }

    get sortExecutors() {
        if (!this.users) return [];

        return this.users
            .filter((user) => UserService.isGeneralContractor(user) || UserService.isSubContractor(user))
            .map((user) => ({ value: user.id, label: user.name }))
            .sort((a, b) => naturalSort(a.label, b.label));
    }

    get sortAuthors() {
        if (!this.users) return [];

        return this.users
            .filter((user) => UserService.isCustomer(user))
            .map((user) => ({
                value: user.id,
                label: user.name,
            }))
            .sort((a, b) => naturalSort(a.label, b.label));
    }

    get findStatus() {
        if (!this.mainPackData?.statuses) return [];

        const statuses = this.mainPackData.statuses.map((name) => ({
            value: name.id,
            label: name.name,
        }));

        statuses.push({
            value: 6,
            label: String(this.$t('canceled')).toLowerCase() as string,
        });

        statuses.push({
            value: 7,
            label: String(this.$t('notAccepted')).toLowerCase() as string,
        });

        return statuses;
    }

    get findAllCities() {
        const cities = this.mainPackData?.cities.map((name) => ({
            value: name.id,
            label: name.name,
        }));

        return sortBy(cities, ['label']);
    }

    get findUserProjects() {
        const projects = this.mainPackData?.projects.map((name) => ({
            value: name.id,
            label: name.project,
        }));

        return sortBy(projects, ['label']);
    }

    get findAllSection() {
        return uniqBy(this.mainPackData?.sections, 'name')
            .map((section) => section.name)
            .sort((a, b) => naturalSort(a, b));
    }

    get findAllFloor() {
        return uniqBy(this.mainPackData?.floors, 'name')
            .map(({ name }) => name)
            .sort((a, b) => naturalSort(a, b));
    }

    get findTypesOfWorks() {
        const typesOfWorks = this.mainPackData?.typesOfWorks.map((typeOfWorks) => ({
            value: typeOfWorks.id,
            label: typeOfWorks.name,
        }));

        return sortBy(typesOfWorks, ['label']);
    }

    get optionTypes() {
        if (!this.mainPackData?.drawingTypes) return [];

        return this.mainPackData.drawingTypes
            .map((type) => ({ value: type.id, label: type.name }))
            .sort((a, b) => naturalSort(a.label, b.label));
    }

    formatOptions(option: keyof FiltersAcceptance) {
        if (!this.filters[option]) return [];
        let options = this.filters[option].map((item) => {
            if (typeof item !== 'string' && typeof item !== 'number') {
                return { value: item.id, label: item.name };
            } else {
                return item;
            }
        });
        options = options.sort((a, b) => {
            if (typeof a !== 'string' && typeof b !== 'string' && typeof a !== 'number' && typeof b !== 'number') {
                return naturalSort(a.label, b.label);
            } else {
                return naturalSort(a as string, b as string);
            }
        });
        return options;
    }

    async created() {
        const response = await getFiltersAcceptance();
        this.filters = response.data;

        getFiltersWarranty().then((response) => {
            this.$set(this.filters, 'flats', response.data.flats);
        });
        this.restoreFilters();
    }

    private applyFilters() {
        const filterSorted: Record<string, string> = {};

        for (const key in this.filter) {
            if (!this.filter[key as keyof IPrescriptionFilter]) continue;

            const value = this.filter[key as keyof IPrescriptionFilter];

            if (key === 'statusId') {
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        filterSorted[key] = value.join(',');
                    }
                } else if (value) {
                    filterSorted[key] = JSON.stringify(value);
                }
            } else if (key === 'deadline') {
                filterSorted[key] = this.dateFormatting(value as string | string[]);
            } else {
                filterSorted[key] = String(value);
            }
        }

        if (JSON.stringify(this.$route.query) !== JSON.stringify(filterSorted)) {
            this.$router.replace({ query: filterSorted });
            this.RESET_VIOLATIONS();
        }
    }

    mounted() {
        this.GET_USERS();
        if (this.mainPackData) {
            if (Object.keys(this.$route.query).length) {
                if (this.$route.query.statusId) {
                    const statusIds = (this.$route.query.statusId as string).split(',');
                    if (statusIds.length) {
                        this.filter.statusId = statusIds.map((el) => parseInt(el));
                    } else {
                        this.filter.statusId = JSON.parse(this.$route.query.statusId.toString());
                    }
                }

                this.filter.cityId = this.$route.query.cityId ? Number(this.$route.query.cityId) : null;
                this.filter.projectId = this.$route.query.projectId ? Number(this.$route.query.projectId) : null;

                if (this.$route.query.object) {
                    this.filter.object = String(this.$route.query.object);
                }

                this.filter.typeOfWorkId = this.$route.query.typeOfWorkId
                    ? Number(this.$route.query.typeOfWorkId)
                    : null;
                this.filter.drawingTypeId = this.$route.query.drawingTypeId
                    ? Number(this.$route.query.drawingTypeId)
                    : null;

                this.filter.subcontractorId = this.$route.query.subcontractorId
                    ? Number(this.$route.query.subcontractorId)
                    : null;

                if (this.$route.query.floorId) {
                    this.filter['section.name'] = find(this.mainPackData.floors, [
                        'id',
                        this.$route.query.floorId,
                    ])!.name;
                }

                if (this.$route.query.sectionId) {
                    const sectionId = Number(this.$route.query.sectionId);
                    if (!isNaN(sectionId)) {
                        this.filter['section.name'] = find(this.mainPackData.sections, ['id', sectionId])!.name;
                    }
                }

                if (this.$route.query['section.name']) {
                    this.filter['section.name'] = String(this.$route.query['section.name']);
                }

                if (this.$route.query['floor.name']) {
                    this.filter['floor.name'] = String(this.$route.query['floor.name']);
                }

                if (this.$route.query.deadline) {
                    this.filter.deadline = String(this.$route.query.deadline).split(',');
                }

                this.filter.generalContractorId = this.$route.query.generalContractorId
                    ? Number(this.$route.query.generalContractorId)
                    : null;
                this.filter.authorId = this.$route.query.authorId ? Number(this.$route.query.authorId) : null;
                this.filter['executor.id'] = this.$route.query['executor.id']
                    ? Number(this.$route.query['executor.id'])
                    : null;

                this.filter.realtyType = this.$route.query.realtyType ? String(this.$route.query.realtyType) : '';
                this.filter.customerId = this.$route.query.customerId ? Number(this.$route.query.customerId) : null;
                this.filter.buildingNumber = this.$route.query.buildingNumber
                    ? String(this.$route.query.buildingNumber)
                    : '';
                this.filter.postalNumber = this.$route.query.postalNumber ? String(this.$route.query.postalNumber) : '';

                this.filter.apartmentNumber = this.$route.query.apartmentNumber
                    ? Number(this.$route.query.apartmentNumber)
                    : null;
            } else {
                this.restoreFilters();
            }
        }
    }

    restoreFilters() {
        const savedFilters = localStorage.getItem('prescriptionFilters');
        if (savedFilters) {
            try {
                const parsedFilters = JSON.parse(savedFilters);
                this.filter = { ...this.defaultFiltersModel, ...parsedFilters };
                this.applyFilters();
            } catch (error) {
                ErrorService.handleApiError(error);
            }
        }
    }

    saveFilters() {
        localStorage.setItem('prescriptionFilters', JSON.stringify(this.filter));
    }

    /**
     * форматирование даты к формату "1970-01-01,1970-01-01"
     * @param {String|String[]} dateRange - дата для форматирования
     * @returns {String} дата
     */
    dateFormatting(dateRange: string | string[]) {
        const fmt = 'yyyy-MM-dd';

        if (Array.isArray(dateRange)) {
            return dateRange.join(',');
        } else {
            const dateString = dateRange as string;
            const dateArray = dateString.split(' ');

            if (dateArray.length > 1) {
                dateArray.splice(1, 1);
                return dateArray.join(',');
            } else {
                const startDate = DateTime.fromFormat(dateString, fmt);
                const endDate = startDate.plus({ day: 1 });

                return `${startDate.toFormat(fmt)},${endDate.toFormat(fmt)}`;
            }
        }
    }

    /**
     * Обнуление переменных фильтра
     */
    onClear() {
        this.filter = cloneDeep(this.defaultFiltersModel);
        localStorage.removeItem('prescriptionFilters');

        if (Object.keys(this.$route.query).length) {
            if (this.isMobile) this.hideFilter();

            this.RESET_VIOLATIONS();
            this.$router.replace({ name: 'prescription.list' });
        }
    }

    onFind() {
        const filterSorted: {
            'authorId'?: string;
            'cityId'?: string;
            'projectId'?: string;
            'object'?: string;
            'statusId'?: string;
            'typeOfWorkId'?: string;
            'section.name'?: string;
            'floor.name'?: string;
            'executor.id'?: string;
            'generalContractorId'?: string;
            'subcontractorId'?: string;
            'drawingTypeId'?: string;
            'deadline'?: string | string[];
            'realtyType'?: string;
            'customerId'?: string;
            'buildingNumber'?: string;
            'postalNumber'?: string;
            'apartmentNumber'?: string;
        } = {};
        let key: keyof IPrescriptionFilter;

        for (key in this.filter) {
            if (!this.filter[key]) {
                delete this.filter[key];
            } else {
                if (key === 'statusId') {
                    if (Array.isArray(this.filter[key])) {
                        if (this.filter[key]!.length > 0) {
                            filterSorted[key] = this.filter[key]!.join(',');
                        }
                    } else if (this.filter[key]) {
                        filterSorted[key] = JSON.stringify(this.filter[key]);
                    } else {
                        delete this.filter[key];
                    }
                } else if (key === 'deadline') {
                    filterSorted[key] = this.dateFormatting(this.filter[key]!);
                } else {
                    filterSorted[key] = String(this.filter[key]);
                }
            }
        }
        this.saveFilters();
        this.applyFilters();

        this.$emit('onOpenFilter');
        if (this.isMobile) this.hideFilter();

        if (JSON.stringify(this.$route.query) !== JSON.stringify(filterSorted)) {
            this.$router.replace({ query: filterSorted });
            this.RESET_VIOLATIONS();
        }
    }
}
